'use client';

import { PropsWithChildren, useEffect, useMemo } from 'react';
import {
  ANALYTICS_CLICK_ATTR,
  ANALYTICS_VIEW_ATTR,
} from '@/_constants/analytics-attrs';
import { TAnalyticEventParams } from '@/_analytic/_types';
import { useHelpCenterType } from '@/_hooks/use-help-center-type';
import { logDataAttribute } from './_utils/log-data-attribute';

type TProps = PropsWithChildren;

export const AnalyticsWrapper = ({ children }: TProps) => {
  const helpCenterType = useHelpCenterType();
  const commonParams: Partial<TAnalyticEventParams> = useMemo(
    () => ({
      helpCenterType,
    }),
    [helpCenterType],
  );

  useEffect(() => {
    const handleAnalytics = (event: MouseEvent) => {
      const element = (event.target as Element).closest(
        `[${ANALYTICS_CLICK_ATTR}]`,
      );

      if (element) {
        logDataAttribute({
          element,
          attributeName: ANALYTICS_CLICK_ATTR,
          params: commonParams,
        });
      }
    };

    document.addEventListener('click', handleAnalytics);

    return () => {
      document.removeEventListener('click', handleAnalytics);
    };
  }, [commonParams]);

  useEffect(() => {
    const viewedElements = new Set<Element>();

    const intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const target = entry.target;

        if (
          entry.isIntersecting &&
          !viewedElements.has(target) &&
          target.hasAttribute(ANALYTICS_VIEW_ATTR)
        ) {
          viewedElements.add(target);
          logDataAttribute({
            element: target,
            attributeName: ANALYTICS_VIEW_ATTR,
            params: commonParams,
          });
        }
      });
    });

    const attributeMutationObserver = new MutationObserver((records) => {
      records.forEach((record) => {
        if (!record.removedNodes.length) {
          record.removedNodes.forEach((removedNode) => {
            viewedElements.delete(removedNode as Element);
            intersectionObserver.unobserve(removedNode as Element);
          });
        }

        [...(record.target as Element).children].forEach((element) => {
          if (element.hasAttribute(ANALYTICS_VIEW_ATTR)) {
            intersectionObserver.observe(element);
          }
        });
      });
    });

    attributeMutationObserver.observe(document.body, {
      subtree: true,
      childList: true,
      attributeFilter: [ANALYTICS_VIEW_ATTR],
    });

    document
      .querySelectorAll(`[${ANALYTICS_VIEW_ATTR}]`)
      .forEach((element) => intersectionObserver.observe(element));

    return () => {
      attributeMutationObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [commonParams]);

  return children;
};
